import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { SimpleCalendar } from "../ui/simple-calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Checkbox } from "../ui/checkbox";
import { format } from 'date-fns';
import { CalendarIcon, Check, AlertTriangle, Camera, Upload, X, FileText, ImageIcon, Clock } from 'lucide-react'; // Add FileText, ImageIcon, Clock
import { LoadingSpinner } from '../ui/loading-spinner';
import { getAuth } from 'firebase/auth';
import { expenseService } from '../../services/expenseService';
import { useToast } from "../../hooks/use-toast";
import { Alert, AlertDescription } from "../ui/alert";
import { cn } from "../../lib/utils";
import { useTheme } from '../ui/theme-provider';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"; // Add this import
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import SmartReceiptScanner from './SmartReceiptScanner';
import { extractReceiptText, testTesseract } from '../../utils/receiptUtils';

// HMRC mileage rate (pence per mile)
const DEFAULT_MILEAGE_RATE = 45;

const QuickEntryForm = ({ onExpenseSaved, onClearLog, categories, loggedEntries = [] }) => {
  const [loading, setLoading] = useState(false);
  const auth = getAuth();
  const { toast } = useToast();
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const { theme } = useTheme();
    // Add receipt upload states  const [receiptUploading, setReceiptUploading] = useState(false);
  const [receiptUploading, setReceiptUploading] = useState(false);
  const [receiptPreviews, setReceiptPreviews] = useState({}); // Store previews by expense type
  const [showSmartScanner, setShowSmartScanner] = useState(false);
  const [scannerExpenseType, setScannerExpenseType] = useState(null);
  const [ocrConfirmation, setOcrConfirmation] = useState(null); // New state for OCR confirmation
  
  // Add refs for receipt upload
  const fileInputRefs = useRef({
    travel: null,
    parking: null,
    subsistence: null
  });
  
  // Add missing camera and mobile detection variables
  const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const hasCamera = navigator.mediaDevices && navigator.mediaDevices.getUserMedia;
  
  // State for multi-expense entries - Ensure separate enabled flags
  const [multiExpense, setMultiExpense] = useState({
    date: new Date(),
    travel: { enabled: false, travelType: 'mileage', mileage: '', mileageRate: DEFAULT_MILEAGE_RATE, amount: '', description: '', receipt: null },
    parking: { enabled: false, amount: '', description: '', receipt: null },
    subsistence: { enabled: false, amount: '', description: '', receipt: null }
  });

  const formRef = useRef({
    inputs: {},
    focused: null
  });

  // ADD handler to toggle expense type enabled status
  const toggleExpenseType = (type) => {
    if (multiExpense.hasOwnProperty(type)) {
      setMultiExpense(prev => ({
        ...prev,
        [type]: { ...prev[type], enabled: !prev[type].enabled }
      }));
    } else {
      console.error(`Attempted to toggle invalid expense type: ${type}`);
    }
  };

  // Update expense fields - Fix dependency issue
  const updateMultiExpense = useCallback((type, field, value) => {
    setMultiExpense(prev => {
      if (prev.hasOwnProperty(type)) {
        return {
          ...prev,
          [type]: { ...prev[type], [field]: value }
        };
      } else {
        console.error(`Attempted to update invalid expense type: ${type}`);
        return prev;
      }
    });
  }, []); // Remove multiExpense dependency to prevent loops

  // Set date and close calendar
  const handleDateSelect = (date) => {
    console.log("Date selected:", date);
    if (date) {
      setMultiExpense(prev => ({ ...prev, date }));
      setTimeout(() => {
        setDatePickerOpen(false);
      }, 100);
    }
  };

  // Save multiple expenses - ADJUSTED LOGIC
  const saveMultipleExpenses = async () => {
    if (!auth.currentUser) {
      setError('User not authenticated.');
      return;
    }

    setError(null);
    setSuccessMessage("");

    if (!multiExpense.date) {
      setError('Please select a date');
      return;
    }

    // Check if at least one expense type is enabled
    const hasEnabledExpense = multiExpense.travel?.enabled ||
                              multiExpense.parking?.enabled ||
                              multiExpense.subsistence?.enabled;

    if (!hasEnabledExpense) {
      setError('Please enable and fill out at least one expense type');
      return;
    }

    setLoading(true);
    try {
      const expensesToSave = [];
      let validationFailed = false;

      // Process travel expense IF ENABLED
      if (multiExpense.travel?.enabled) {
        const travelData = multiExpense.travel;
        const travelExpense = {
          date: multiExpense.date,
          category: 'travel',
          description: travelData.description || 'Travel expense',
          travelType: travelData.travelType
        };

        // Add receipt data if available - use Firebase Storage URL
        if (travelData.receipt) {
          travelExpense.receiptUrl = travelData.receipt.downloadUrl; // Firebase URL, not blob
          travelExpense.receiptId = travelData.receipt.id;
          travelExpense.receiptStoragePath = travelData.receipt.storagePath;
          travelExpense.receiptName = travelData.receipt.file.name; // Fix: use file.name
          travelExpense.receiptSize = travelData.receipt.compressedSize;
          travelExpense.originalReceiptSize = travelData.receipt.originalSize;
          travelExpense.compressionRatio = travelData.receipt.compressionRatio;
          travelExpense.isPdf = travelData.receipt.isPdf;
          travelExpense.hasReceipt = true;
        }

        let travelAmount = 0;
        if (travelExpense.travelType === 'mileage') {
          const mileage = parseFloat(travelData.mileage);
          const rate = parseFloat(travelData.mileageRate) || DEFAULT_MILEAGE_RATE;
          if (isNaN(mileage) || mileage <= 0) {
            setError('Please enter valid mileage for Travel'); validationFailed = true;
          } else {
            travelAmount = (mileage * rate) / 100;
            travelExpense.mileage = travelData.mileage;
            travelExpense.mileageRate = rate;
          }
        } else { // publicTransport
          const amount = parseFloat(travelData.amount);
          if (isNaN(amount) || amount <= 0) {
            setError('Please enter a valid amount for Travel'); validationFailed = true;
          } else {
            travelAmount = amount;
            travelExpense.amount = travelData.amount;
          }
        }

        if (!validationFailed) {
          travelExpense.totalAmount = travelAmount;
          expensesToSave.push(travelExpense);
        }
      }

      // Process parking expense IF ENABLED and validation hasn't failed
      if (!validationFailed && multiExpense.parking?.enabled) {
        const parkingData = multiExpense.parking;
        const amount = parseFloat(parkingData.amount);
        if (isNaN(amount) || amount <= 0) {
          setError('Please enter a valid amount for Parking'); validationFailed = true;
        } else {
          const parkingExpense = {
            date: multiExpense.date,
            category: 'parking',
            description: parkingData.description || 'Parking fee',
            amount: parkingData.amount,
            totalAmount: amount
          };

          // Add receipt data if available - use Firebase Storage URL
          if (parkingData.receipt) {
            parkingExpense.receiptUrl = parkingData.receipt.downloadUrl; // Firebase URL, not blob
            parkingExpense.receiptId = parkingData.receipt.id;
            parkingExpense.receiptStoragePath = parkingData.receipt.storagePath;
            parkingExpense.receiptName = parkingData.receipt.file.name; // Fix: use file.name
            parkingExpense.receiptSize = parkingData.receipt.compressedSize;
            parkingExpense.originalReceiptSize = parkingData.receipt.originalSize;
            parkingExpense.compressionRatio = parkingData.receipt.compressionRatio;
            parkingExpense.isPdf = parkingData.receipt.isPdf;
            parkingExpense.hasReceipt = true;
          }

          expensesToSave.push(parkingExpense);
        }
      }

      // Process subsistence expense IF ENABLED and validation hasn't failed
      if (!validationFailed && multiExpense.subsistence?.enabled) {
        const subsistenceData = multiExpense.subsistence;
        const amount = parseFloat(subsistenceData.amount);
        if (isNaN(amount) || amount <= 0) {
          setError('Please enter a valid amount for Food/Subsistence'); validationFailed = true;
        } else {
          const subsistenceExpense = {
            date: multiExpense.date,
            category: 'subsistence',
            description: subsistenceData.description || 'Food/Subsistence',
            amount: subsistenceData.amount,
            totalAmount: amount
          };

          // Add receipt data if available - use Firebase Storage URL
          if (subsistenceData.receipt) {
            subsistenceExpense.receiptUrl = subsistenceData.receipt.downloadUrl; // Firebase URL, not blob
            subsistenceExpense.receiptId = subsistenceData.receipt.id;
            subsistenceExpense.receiptStoragePath = subsistenceData.receipt.storagePath;
            subsistenceExpense.receiptName = subsistenceData.receipt.file.name; // Fix: use file.name
            subsistenceExpense.receiptSize = subsistenceData.receipt.compressedSize;
            subsistenceExpense.originalReceiptSize = subsistenceData.receipt.originalSize;
            subsistenceExpense.compressionRatio = subsistenceData.receipt.compressionRatio;
            subsistenceExpense.isPdf = subsistenceData.receipt.isPdf;
            subsistenceExpense.hasReceipt = true;
          }

          expensesToSave.push(subsistenceExpense);
        }
      }

      // If any validation failed, stop processing
      if (validationFailed) {
        setLoading(false);
        return;
      }

      // If no expenses were actually added, show error
      if (expensesToSave.length === 0) {
         setError('Please ensure enabled expense types have valid amounts/details.');
         setLoading(false);
         return;
      }

      // Save all valid expenses
      const savedExpenses = [];
      for (const expense of expensesToSave) {
        if (typeof expense.totalAmount !== 'number' || isNaN(expense.totalAmount)) {
           console.error('Invalid totalAmount detected before saving:', expense);
           throw new Error('Internal error: Invalid expense amount calculated.');
        }
        const savedExpense = await expenseService.addExpense(auth.currentUser.uid, expense);
        savedExpenses.push(savedExpense);
      }

      // Reset form - Keep date, disable toggles, clear fields AND clear receipts
      setMultiExpense({
        date: multiExpense.date, // Keep the selected date
        travel: { enabled: false, travelType: 'mileage', mileage: '', mileageRate: DEFAULT_MILEAGE_RATE, amount: '', description: '', receipt: null },
        parking: { enabled: false, amount: '', description: '', receipt: null },
        subsistence: { enabled: false, amount: '', description: '', receipt: null }
      });

      // Clear receipt previews
      setReceiptPreviews({});

      if (onExpenseSaved) {
        onExpenseSaved(savedExpenses);
      }

      toast({
        title: "Expenses Saved",
        description: `Successfully added ${expensesToSave.length} expense(s)`,
        variant: "success",
        duration: 3000,
      });
      setSuccessMessage(`Successfully added ${expensesToSave.length} expense(s)`);
      setTimeout(() => setSuccessMessage(""), 5000);

    } catch (error) {
      console.error('Error saving multiple expenses:', error);
      setError(`Failed to save expenses: ${error.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  // Compact input field component
  const CompactInput = React.forwardRef(({
    label,
    value,
    onChange,
    placeholder,
    type="text",
    className = "",
    id
  }, ref) => {
    const inputId = id || `input-${label.replace(/\s+/g, '-').toLowerCase()}-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <div className="flex flex-col">
        <Label
          htmlFor={inputId}
          className={cn(
            "text-xs mb-0.5",
            theme === 'light' ? 'text-gray-700' : 'text-gray-300'
          )}
        >
          {label}
        </Label>
        <Input
          id={inputId}
          type={type}
          value={value}
          onChange={(e) => {
            if (formRef.current) {
              formRef.current.focused = inputId;
            }
            onChange(e);
          }}
          className={cn(
            "h-8 px-2 py-1 text-sm",
            className,
            // Add theme-specific input styles
            theme === 'light'
              ? 'bg-white border-gray-300 text-black placeholder-gray-400 focus:border-purple-500 focus:ring-purple-500'
              : 'bg-[#1F1F2E] border-purple-500/30 text-white placeholder-gray-500 focus:border-purple-500 focus:ring-purple-500'
          )}
          placeholder={placeholder}
          ref={(el) => {
            if (el && formRef.current) {
              formRef.current.inputs[inputId] = el;
            }
          }}
        />
      </div>
    );
  });

  // Maintain focus after state updates
  useEffect(() => {
    if (formRef.current?.focused && formRef.current?.inputs?.[formRef.current.focused]) {
      formRef.current.inputs[formRef.current.focused].focus();
    }
  });

  // Receipt upload component for each expense type - MOVE THIS BEFORE renderTravelInputs
  const ReceiptUploadSection = ({ expenseType, label }) => {
    const finalReceipt = multiExpense[expenseType]?.receipt;
    const tempPreview = receiptPreviews[expenseType];
    
    return (
      <div className="mt-2">
        <Label className={cn(
          "text-xs mb-1 block",
          theme === 'light' ? 'text-gray-700' : 'text-gray-300'
        )}>
          Receipt (Optional)
        </Label>
        
        {finalReceipt ? (
          // Display successfully uploaded receipt
          <div className="flex items-start gap-2">
            <div className="relative w-auto">
              {finalReceipt.isPdf ? (
                <div className="w-[80px] h-auto p-2 flex flex-col items-center justify-center border rounded bg-gray-100 dark:bg-gray-700 text-muted-foreground">
                  <FileText className="h-8 w-8 mb-1 flex-shrink-0" />
                  <span className="text-[10px] leading-tight break-all line-clamp-2 text-center">
                    {finalReceipt.file.name}
                  </span>
                </div>
              ) : (
                <img 
                  src={finalReceipt.preview.url} // URL from when it was a blob
                  alt={`${label} receipt`} 
                  className="max-w-[80px] h-auto object-contain rounded border block border-gray-300 dark:border-gray-600"
                />
              )}
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="absolute -top-2 -right-2 h-5 w-5 p-0 bg-black/60 hover:bg-black/80 rounded-full z-10"
                onClick={() => removeReceipt(expenseType)}
              >
                <X className="h-3 w-3 text-white" />
              </Button>
            </div>
            <div className="text-xs text-muted-foreground pt-1">
              {(finalReceipt.compressedSize / 1024).toFixed(0)}KB
            </div>
          </div>
        ) : tempPreview ? (
          // Display temporary preview while uploading or before saving
          <div className="flex items-start gap-2">
            <div className="relative w-auto">
              {tempPreview.type === 'pdf' ? (
                <div className="w-[80px] h-auto p-2 flex flex-col items-center justify-center border rounded bg-gray-100 dark:bg-gray-700 text-muted-foreground">
                  <FileText className="h-8 w-8 mb-1 flex-shrink-0" />
                  <span className="text-[10px] leading-tight break-all line-clamp-2 text-center">
                    {tempPreview.name}
                  </span>
                </div>
              ) : (
                <img 
                  src={tempPreview.url} 
                  alt={`${label} preview`} 
                  className={`max-w-[80px] h-auto object-contain rounded border block border-gray-300 dark:border-gray-600 ${receiptUploading ? 'opacity-70' : ''}`}
                />
              )}
               <Button // Allow removing temporary preview
                type="button"
                size="icon"
                variant="ghost"
                className="absolute -top-2 -right-2 h-5 w-5 p-0 bg-black/60 hover:bg-black/80 rounded-full z-10"
                onClick={() => removeReceipt(expenseType)} // removeReceipt clears both final and temp
              >
                <X className="h-3 w-3 text-white" />
              </Button>
            </div>
            {receiptUploading && (
              <div className="pt-1">
                <LoadingSpinner size="small" />
                <span className="ml-1 text-xs text-muted-foreground">Uploading...</span>
              </div>
            )}
             {!receiptUploading && tempPreview.size && ( // Show size for temp preview if not uploading
                <div className="text-xs text-muted-foreground pt-1">
                    {(tempPreview.size / 1024).toFixed(0)}KB
                </div>
            )}
          </div>
        ) : receiptUploading ? (
          // Generic "Processing..." if an upload is globally active and no preview for this section
          <div className={cn(
            "flex items-center justify-center h-9 rounded-md border", 
            theme === 'light' ? 'bg-gray-50 border-gray-200' : 'bg-gray-700/30 border-gray-600'
          )}>
            <LoadingSpinner size="small" />
            <span className="ml-2 text-xs text-muted-foreground">Processing...</span>
          </div>
        ) : (          // Show Camera/Upload buttons in a grouped container
          <div className={cn(
            "p-2 rounded-lg border",
            theme === 'light' ? 'bg-gray-50/50 border-gray-200' : 'bg-gray-700/20 border-gray-600'
          )}>
            <div className="flex gap-2">              {hasCamera && isMobile && (
                <div className="flex flex-col">
                  <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => startCamera(expenseType)}
                  disabled={receiptUploading}
                  className={cn(
                    "flex items-center gap-1 text-xs px-3 py-1.5 border",
                    theme === 'light' ? 'bg-white border-purple-300 text-purple-700 hover:bg-purple-50' : 'bg-gray-800 border-purple-500/50 text-purple-300 hover:bg-purple-900/30'
                  )}
                >                    <Clock className="h-3 w-3" />
                    Smart Capture
                  </Button>
                  <div className={cn(
                    "text-xs italic mt-1 ml-1",
                    theme === 'light' ? 'text-purple-500/70' : 'text-purple-400/70'
                  )}>
                    AI-powered (Beta)
                  </div>
              </div>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRefs.current[expenseType]?.click()}
              disabled={receiptUploading}
              className={cn(
                "flex items-center gap-1 text-xs px-3 py-1.5 border",
                theme === 'light' ? 'bg-white border-purple-300 text-purple-700 hover:bg-purple-50' : 'bg-gray-800 border-purple-500/50 text-purple-300 hover:bg-purple-900/30'
              )}
            >
              <Upload className="h-3 w-3" />
              Upload
            </Button>
            <input
              ref={el => fileInputRefs.current[expenseType] = el}
              type="file"
              accept="image/*,application/pdf" // Allow PDF
              capture={isMobile ? "environment" : undefined}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) processReceiptImage(file, expenseType);              }}
            />
            </div>
          </div>
        )}
      </div>
    );
  };

  // Travel inputs section
  const renderTravelInputs = useMemo(() => {
    if (!multiExpense.travel?.enabled) return null;

    return (
      <div className="mb-3 border-t border-border pt-3">
        <Label className={cn(
          "text-base font-bold mb-2 block",
          theme === 'light' ? 'text-gray-800' : 'text-white'
        )}>
          Travel Details
        </Label>
        {/* Change from justify-between to justify-center with gap */}
        <div className="flex items-center justify-center gap-4">
          {/* Updated radio styling with smaller text and centered layout */}
          <label 
            htmlFor="quick-travel-mileage" 
            className={cn(
              "flex items-center cursor-pointer rounded-md px-2 py-1 text-xs transition-colors border",
              multiExpense.travel.travelType === 'mileage'
                ? theme === 'light' 
                  ? "bg-purple-100 text-purple-700 font-medium border-purple-300" 
                  : "bg-purple-900/20 text-purple-300 font-medium border-purple-500/30"
                : theme === 'light'
                  ? "text-gray-700 bg-white border-gray-200 hover:bg-gray-100"
                  : "text-gray-300 bg-[#23213a] border-gray-700 hover:bg-[#2A2A3E]"
            )}
          >
            <input
              type="radio"
              id="quick-travel-mileage"
              name="quickTravelType"
              checked={multiExpense.travel.travelType === 'mileage'}
              onChange={() => updateMultiExpense('travel', 'travelType', 'mileage')}
              className={cn(
                "mr-1 h-3 w-3", 
                theme === 'light' ? "accent-purple-700" : "accent-purple-500"
              )}
            />
            Mileage
          </label>
          
          {/* Updated radio styling with smaller text */}
          <label 
            htmlFor="quick-travel-public" 
            className={cn(
              "flex items-center cursor-pointer rounded-md px-2 py-1 text-xs transition-colors border",
              multiExpense.travel.travelType === 'publicTransport'
                ? theme === 'light' 
                  ? "bg-purple-100 text-purple-700 font-medium border-purple-300" 
                  : "bg-purple-900/20 text-purple-300 font-medium border-purple-500/30"
                : theme === 'light'
                  ? "text-gray-700 bg-white border-gray-200 hover:bg-gray-100"
                  : "text-gray-300 bg-[#23213a] border-gray-700 hover:bg-[#2A2A3E]"
            )}
          >
            <input
              type="radio"
              id="quick-travel-public"
              name="quickTravelType"
              checked={multiExpense.travel.travelType === 'publicTransport'}
              onChange={() => updateMultiExpense('travel', 'travelType', 'publicTransport')}
              className={cn(
                "mr-1 h-3 w-3", 
                theme === 'light' ? "accent-purple-700" : "accent-purple-500"
              )}
            />
            Transport
          </label>
        </div>

        <div className="mt-2 grid grid-cols-3 gap-1.5">
          {multiExpense.travel.travelType === 'mileage' ? (
            <>
              <CompactInput
                id="travel-mileage"
                label="Miles"
                type="number"
                value={multiExpense.travel.mileage}
                onChange={(e) => updateMultiExpense('travel', 'mileage', e.target.value)}
                placeholder="Miles"
                className="col-span-1"
              />
              <CompactInput
                id="travel-rate"
                label="Rate (p/mile)"
                type="number"
                value={multiExpense.travel.mileageRate}
                onChange={(e) => updateMultiExpense('travel', 'mileageRate', e.target.value)}
                placeholder={String(DEFAULT_MILEAGE_RATE)}
                className="col-span-1"
              />
              <CompactInput
                id="travel-desc"
                label="Notes"
                value={multiExpense.travel.description}
                onChange={(e) => updateMultiExpense('travel', 'description', e.target.value)}
                placeholder="Optional"
                className="col-span-1"
              />
            </>
          ) : (
            <>
              <CompactInput
                id="travel-amount"
                label="Amount (£)"
                type="number"
                value={multiExpense.travel.amount}
                onChange={(e) => updateMultiExpense('travel', 'amount', e.target.value)}
                className="col-span-1"
                placeholder="e.g. 12.50"
              />
              <CompactInput
                id="travel-transport-desc"
                label="Notes"
                value={multiExpense.travel.description}
                onChange={(e) => updateMultiExpense('travel', 'description', e.target.value)}
                placeholder="Optional"
                className="col-span-2"
              />
            </>
          )}
        </div>
        
        {/* Add receipt upload for travel */}
        <ReceiptUploadSection expenseType="travel" label="Travel" />
      </div>
    ); // Explicitly add semicolon here
  }, [multiExpense.travel, updateMultiExpense, theme, receiptPreviews, receiptUploading]); // Added receiptUploading to dependency array
  // Receipt upload functionality with OCR
  const processReceiptImage = async (file, expenseType) => {
    setReceiptUploading(true);
    
    try {
      // Validate file type
      if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
        throw new Error('Please select an image or PDF file');
      }

      // Check authentication first
      if (!auth.currentUser) {
        throw new Error('User not authenticated. Please log in again.');
      }

      // Create preview for immediate UI feedback
      const isPdf = file.type === 'application/pdf';
      let previewData;

      if (isPdf) {
        previewData = { type: 'pdf', name: file.name, size: file.size };
      } else {
        const imagePreviewUrl = URL.createObjectURL(file);
        previewData = { type: 'image', url: imagePreviewUrl, name: file.name, size: file.size };
      }
      setReceiptPreviews(prev => ({ ...prev, [expenseType]: previewData }));

      // Compress image for storage efficiency (only if not PDF)
      const compressedFile = isPdf ? file : await compressReceiptImage(file);
      
      // Generate unique receipt ID
      const fileExtension = isPdf ? 'pdf' : 'jpg';
      const receiptId = `receipt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Upload to Firebase Storage
      const storage = getStorage();
      const storagePath = `users/${auth.currentUser.uid}/receipts/${receiptId}.${fileExtension}`;
      const storageRef = ref(storage, storagePath);
      
      // Upload the compressed file with metadata
      const metadata = {
        contentType: file.type,
        customMetadata: {
          'originalSize': file.size.toString(),
          'compressedSize': compressedFile.size.toString(),
          'uploadedBy': auth.currentUser.uid,
          'expenseType': expenseType,
          'uploadTime': new Date().toISOString(),
          'isPdf': isPdf.toString()
        }
      };

      await uploadBytes(storageRef, compressedFile, metadata);
      
      // Get the download URL
      const downloadUrl = await getDownloadURL(storageRef);
      
      // Update expense state with receipt data including Firebase URL
      setMultiExpense(prev => ({
        ...prev,
        [expenseType]: {
          ...prev[expenseType],
          receipt: {
            id: receiptId,
            file: { name: compressedFile.name, type: file.type, size: compressedFile.size },
            preview: previewData,
            downloadUrl: downloadUrl, 
            storagePath: storagePath,
            originalSize: file.size,
            compressedSize: compressedFile.size,
            compressionRatio: isPdf ? '0' : ((file.size - compressedFile.size) / file.size * 100).toFixed(1),
            isPdf: isPdf
          }
        }
      }));

      // Add OCR processing for images (not PDFs)
      let ocrResults = null;
      if (!isPdf) {
        try {
          ocrResults = await extractReceiptText(file);
          
          // Check if OCR returned an error
          if (ocrResults.error) {
            throw new Error(ocrResults.error);
          }
            // Show OCR results to user via confirmation dialog
          if (ocrResults.amount || ocrResults.vendor) {
            setOcrConfirmation({
              expenseType,
              amount: ocrResults.amount ? {
                value: ocrResults.amount.toFixed(2),
                confidence: ocrResults.allAmounts?.[0]?.confidence || ocrResults.confidence || 0
              } : null,
              vendor: ocrResults.vendor ? {
                value: ocrResults.vendor,
                confidence: ocrResults.confidence || 0
              } : null,              onApprove: (approvedData) => {
                console.log('OCR Approval - applying data:', approvedData); // Debug log
                if (approvedData.amount) {
                  console.log('Updating amount:', approvedData.amount);
                  updateMultiExpense(expenseType, 'amount', approvedData.amount);
                }
                if (approvedData.vendor) {
                  console.log('Updating description:', approvedData.vendor);
                  updateMultiExpense(expenseType, 'description', approvedData.vendor);
                }
                toast({
                  title: "OCR Values Applied",
                  description: `Updated ${Object.keys(approvedData).length} field(s)`,
                  variant: "success",
                  duration: 3000,
                });
                setOcrConfirmation(null);
              },
              onCancel: () => setOcrConfirmation(null)
            });
          }
          
        } catch (ocrError) {
          console.warn('OCR processing failed:', ocrError);
          toast({
            title: "OCR processing failed",
            description: "Receipt uploaded but text detection failed.",
            variant: "warning",
            duration: 4000,
          });
        }
      }

      // Include OCR data in receipt object
      setMultiExpense(prev => ({
        ...prev,
        [expenseType]: {
          ...prev[expenseType],
          receipt: {
            ...prev[expenseType].receipt,
            ocrData: ocrResults,
            autoFilledAmount: ocrResults?.amount || null,
            detectedVendor: ocrResults?.vendor || null
          }
        }
      }));

      toast({
        title: `${isPdf ? 'PDF' : 'Receipt'} uploaded`,
        description: `${isPdf ? 'PDF' : 'Receipt'} uploaded (${(file.size/1024).toFixed(0)}KB${!isPdf ? ` → ${(compressedFile.size/1024).toFixed(0)}KB` : ''})`,
        variant: "success",
      });

    } catch (error) {
      console.error('Error processing receipt:', error);
      
      // Provide more specific error messages
      let errorMessage = error.message;
      if (error.code === 'storage/unauthorized') {
        errorMessage = 'Storage permission denied. Please check if Storage rules are properly configured and deployed.';
      } else if (error.code === 'storage/quota-exceeded') {
        errorMessage = 'Storage quota exceeded. Please contact support.';
      } else if (error.code === 'storage/invalid-format') {
        errorMessage = 'Invalid file format. Please select a valid image file.';
      } else if (error.code === 'storage/retry-limit-exceeded') {
        errorMessage = 'Upload failed after multiple attempts. Please try again.';
      }

      toast({
        title: "Upload failed",
        description: errorMessage,
        variant: "destructive",
      });
      
      // Remove preview on error
      setReceiptPreviews(prev => {
        const updated = { ...prev };
        delete updated[expenseType];
        return updated;
      });
    } finally {
      setReceiptUploading(false);
    }
  };

  // Add image compression function
  const compressReceiptImage = async (file) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions (max 1000px width/height)
        const maxSize = 1000;
        let { width, height } = img;
        
        if (width > height && width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        } else if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob((blob) => {
          const compressedFile = new File([blob], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now()
          });
          resolve(compressedFile);
        }, 'image/jpeg', 0.7); // 70% quality
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const startCamera = (expenseType) => {
    setScannerExpenseType(expenseType);
    setShowSmartScanner(true);
  };

  const handleSmartCapture = async (file) => {
    if (scannerExpenseType) {
      await processReceiptImage(file, scannerExpenseType);
    }
    closeSmartScanner();
  };
  const closeSmartScanner = () => {
    setShowSmartScanner(false);
    setScannerExpenseType(null);
    
    // Small delay to ensure proper cleanup
    setTimeout(() => {
      console.log('Smart scanner closed and reset');
    }, 100);
  };

  const removeReceipt = (expenseType) => {
    setMultiExpense(prev => ({
      ...prev,
      [expenseType]: { ...prev[expenseType], receipt: null }
    }));

    setReceiptPreviews(prev => {
      const updated = { ...prev };
      delete updated[expenseType];
      return updated;
    });
  };

  // Maintain focus after state updates
  useEffect(() => {
    if (formRef.current?.focused && formRef.current?.inputs?.[formRef.current.focused]) {
      formRef.current.inputs[formRef.current.focused].focus();
    }
  });

  return (
    <Card className={cn(
      "shadow-lg border-0",
      theme === 'light' ? "bg-white" : "bg-[#2A2A3E]"    )}>
      <div className="space-y-2 flex-grow overflow-y-auto pb-16">
        {/* Error alert */}
        {error && (
          <Alert variant="destructive" className="mb-4 bg-red-900/20 border-red-500/50">
            <AlertTriangle className="h-4 w-4 mr-2" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Success message */}
        {successMessage && (
          <Alert className="mb-4 bg-green-900/20 border-green-500/50">
            <Check className="h-4 w-4 mr-2 text-green-500" />
            <AlertDescription className="text-green-500">{successMessage}</AlertDescription>
          </Alert>
        )}

        {/* Date picker - Ensure Label uses theme-aware color */}
        <div>
          {/* Use theme-aware color */}
          <Label htmlFor="date" className={cn(
            "mb-1 block",
            theme === 'light' ? 'text-gray-800' : 'text-white'
          )}>Date</Label>
          <div className="relative">
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal border-app-accent-purple",
                    theme === 'light'
                      ? "bg-white text-black hover:bg-gray-50 hover:text-black focus:bg-white focus:text-black active:bg-gray-100 active:text-black" 
                      : "bg-[#1F1F2E] text-white hover:bg-[#2a2a3e] focus:bg-[#1F1F2E] active:bg-[#2a2a3e]",
                    !multiExpense.date && (theme === 'light' ? "text-gray-500" : "text-gray-400")
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {multiExpense.date ? format(multiExpense.date, "PPP") : "Select date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 border-app-accent-purple">
                <SimpleCalendar
                  value={multiExpense.date}
                  onChange={handleDateSelect}
                  className="border-none"
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Expense type toggles */}
        <div className="mb-3">
          <Label className={cn(
            "text-xs font-semibold opacity-90 mb-1 block",
            theme === 'light' ? 'text-gray-800' : 'text-white'
          )}>
            Select Expense Types
          </Label>
          <div className="grid grid-cols-3 gap-1.5">
            { [
              { value: 'travel', label: 'Travel' },
              { value: 'parking', label: 'Parking' },
              { value: 'subsistence', label: 'Food' },
            ].map((item) => {
              const isSelected = multiExpense[item.value]?.enabled ?? false;
              return (
                <div key={item.value}>
                  {/* Label wrapping Checkbox for better accessibility */}
                  <Label
                    htmlFor={`expense-type-${item.value}`}
                    className={cn(
                      "flex items-center gap-2 border rounded-lg p-2 cursor-pointer transition-all",
                      "border-app-accent-purple", // Always purple border
                      // No extra style needed on label when selected
                      isSelected
                        ? "" 
                        : theme === 'light'
                          ? "bg-white hover:bg-purple-50" // Unselected Light
                          : "bg-[#23213a] hover:bg-[#2A2A3E]", // Unselected Dark
                      "hover:border-app-accent-purple"
                    )}
                  >
                    {/* The key change here: Using forwardRef + inline styling for direct control */}
                    <Checkbox
                      id={`expense-type-${item.value}`}
                      checked={isSelected}
                      onCheckedChange={() => toggleExpenseType(item.value)}
                      className="border-2 border-app-accent-purple"
                      // Apply direct inline style to control SVG (highest specificity)
                      style={{
                        // Force checkbox background to be transparent 
                        backgroundColor: 'transparent',
                        // When checked, force SVG stroke to be visible against any background
                        '--checkbox-check-color': theme === 'light' ? '#6B46C1' : 'white',
                      }}
                    />
                    <span className="font-semibold opacity-90 select-none text-[var(--app-accent-purple)]">
                      {item.label}
                    </span>
                  </Label>
                  
                  {/* Add direct override styles for checkbox SVG - will apply to all checkboxes in this component */}
                  <style jsx>{`
                    /* Target the SVG directly with high specificity */
                    #expense-type-${item.value}[data-state="checked"] svg {
                      stroke: var(--checkbox-check-color) !important;
                      color: var(--checkbox-check-color) !important;
                    }
                  `}</style>
                </div>
              );
            })}
          </div>
        </div>

        {/* Render inputs based on enabled flags */}
        {renderTravelInputs}

        {/* Parking expense section - Render based on multiExpense.parking.enabled */}
        {multiExpense.parking?.enabled && (
          <div className="mb-3 border-t border-border pt-3">
            {/* Use theme-aware color */}
            <Label className={cn(
              "text-base font-bold mb-2 block",
              theme === 'light' ? 'text-gray-800' : 'text-white'
            )}>
              Parking Details
            </Label>
            <div className="mt-2 grid grid-cols-2 gap-1.5">
              <CompactInput
                id="parking-amount"
                label="Amount (£)"
                type="number"
                value={multiExpense.parking.amount}
                onChange={(e) => updateMultiExpense('parking', 'amount', e.target.value)}
                className="col-span-1"
                placeholder="e.g. 5.00"
              />
              <CompactInput
                id="parking-desc"
                label="Notes"
                value={multiExpense.parking.description}
                onChange={(e) => updateMultiExpense('parking', 'description', e.target.value)}
                placeholder="Optional"
                className="col-span-1"
              />
            </div>
            <ReceiptUploadSection expenseType="parking" label="Parking" />
          </div>
        )}

        {/* Subsistence expense section - Render based on multiExpense.subsistence.enabled */}
        {multiExpense.subsistence?.enabled && (
          <div className="mb-3 border-t border-border pt-3">
            {/* Use theme-aware color */}
            <Label className={cn(
              "text-base font-bold mb-2 block",
              theme === 'light' ? 'text-gray-800' : 'text-white'
            )}>
              Food Details
            </Label>
            <div className="mt-2 grid grid-cols-2 gap-1.5">
              <CompactInput
                id="food-amount"
                label="Amount (£)"
                type="number"
                value={multiExpense.subsistence.amount}
                onChange={(e) => updateMultiExpense('subsistence', 'amount', e.target.value)}
                className="col-span-1"
                placeholder="e.g. 8.50"
              />
              <CompactInput
                id="food-desc"
                label="Notes"
                value={multiExpense.subsistence.description}
                onChange={(e) => updateMultiExpense('subsistence', 'description', e.target.value)}
                placeholder="Optional"
                className="col-span-1"
              />
            </div>
            <ReceiptUploadSection expenseType="subsistence" label="Food" />
          </div>        )}

        {/* OCR Confirmation Dialog */}
        {ocrConfirmation && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className={cn(
              "bg-white dark:bg-[#2A2A3E] rounded-lg border shadow-lg max-w-md w-full",
              "border-purple-200 dark:border-purple-500/30"
            )}>
              <div className="p-4 border-b border-purple-200 dark:border-purple-500/30">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Receipt Scan Results
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Review and approve the detected values
                </p>
              </div>
              
              <div className="p-4 space-y-4">                {ocrConfirmation.amount && (
                  <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/30">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        Amount: £{ocrConfirmation.amount.value}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {ocrConfirmation.amount.confidence.toFixed(0)}% confidence
                      </div>
                    </div>
                    <input 
                      type="checkbox"
                      id="approve-amount"
                      defaultChecked={true}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-purple-300 rounded"
                    />
                  </div>
                )}
                
                {ocrConfirmation.vendor && (
                  <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/30">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        Vendor: {ocrConfirmation.vendor.value}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {ocrConfirmation.vendor.confidence.toFixed(0)}% confidence
                      </div>
                    </div>
                    <input 
                      type="checkbox"
                      id="approve-vendor"
                      defaultChecked={ocrConfirmation.vendor.confidence > 70}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-purple-300 rounded"
                    />
                  </div>
                )}
                
                {!ocrConfirmation.amount && !ocrConfirmation.vendor && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    No values were detected from this receipt
                  </div>
                )}
              </div>
              
              <div className="p-4 border-t border-purple-200 dark:border-purple-500/30 flex gap-2">
                <Button
                  variant="outline"
                  onClick={ocrConfirmation.onCancel}
                  className="flex-1"
                >
                  Cancel
                </Button>                <Button
                  onClick={() => {
                    const approvedData = {};
                    
                    if (ocrConfirmation.amount) {
                      const amountCheckbox = document.getElementById('approve-amount');
                      if (amountCheckbox?.checked) {
                        approvedData.amount = ocrConfirmation.amount.value;
                      }
                    }
                    
                    if (ocrConfirmation.vendor) {
                      const vendorCheckbox = document.getElementById('approve-vendor');
                      if (vendorCheckbox?.checked) {
                        approvedData.vendor = ocrConfirmation.vendor.value;
                      }
                    }
                    
                    console.log('Apply button clicked - approved data:', approvedData); // Debug log
                    ocrConfirmation.onApprove(approvedData);
                  }}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                >
                  Apply Selected
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Smart scanner component */}
        <SmartReceiptScanner
          isOpen={showSmartScanner}
          onCaptureComplete={handleSmartCapture}
          onClose={closeSmartScanner}
        />

        {/* Save button */}
        <Button
          onClick={saveMultipleExpenses}
          className="mt-auto w-full app-accent-purple-bg hover:app-accent-purple-hover text-white sticky bottom-0 shadow-lg"
          disabled={loading}
        >
          {loading ? <LoadingSpinner size="small" /> : "Save Expenses"}
        </Button>
      </div>
    </Card>
  );
};

export default React.memo(QuickEntryForm);